import React, { useEffect } from 'react';
import { FaFileContract } from 'react-icons/fa';

const Terms = () => {
  useEffect(() => {
    document.title = "Terms of Service - SastaMastaRide";
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <FaFileContract className="text-blue-600 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Terms of Service</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-700">
          <div className="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-600">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> This website is operated by <strong>SastaMastaRide</strong>, managed by <strong>RKTECH Solutions</strong>.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing and using SastaMastaRide ("the Platform", "we", "us", "our"), you accept and agree to be bound by 
              these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
            </p>
            <p>
              We reserve the right to modify these Terms at any time. Continued use of the Platform after changes 
              constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Description of Service</h2>
            <p className="mb-4">
              SastaMastaRide is a ridesharing platform that connects riders (vehicle owners) with passengers seeking 
              shared transportation. We facilitate connections but are not a transportation provider, taxi service, 
              or common carrier.
            </p>
            <p>
              The Platform enables users to:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Find and book shared rides on bikes, scooters, and cars</li>
              <li>Offer rides and share transportation costs</li>
              <li>Connect with other users for ridesharing purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. User Accounts</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">3.1 Registration</h3>
            <p className="mb-4">
              To use our services, you must create an account by providing accurate, current, and complete information. 
              You are responsible for maintaining the confidentiality of your account credentials.
            </p>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">3.2 Eligibility</h3>
            <p className="mb-4">
              You must be at least 18 years old to use our Platform. By using the Platform, you represent and warrant 
              that you meet this age requirement.
            </p>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">3.3 Account Responsibility</h3>
            <p>
              You are responsible for all activities that occur under your account. You must immediately notify us of 
              any unauthorized use of your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Rider Requirements</h2>
            <p className="mb-4">Riders must:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Be at least 18 years old with a valid driving license</li>
              <li>Complete the verification process with valid documents</li>
              <li>Own or have legal authority to use the vehicle</li>
              <li>Maintain valid vehicle registration and insurance</li>
              <li>Ensure vehicle is in safe and roadworthy condition</li>
              <li>Comply with all applicable traffic and transportation laws</li>
            </ul>
            <p>
              We reserve the right to verify rider credentials and may suspend or terminate accounts that fail to meet 
              these requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Booking and Payments</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">5.1 Booking Process</h3>
            <p className="mb-4">
              When you book a ride, you enter into a direct agreement with the rider. SastaMastaRide facilitates the 
              connection but is not a party to the transportation agreement.
            </p>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">5.2 Payment</h3>
            <p className="mb-4">
              Payments are processed through our secure payment gateway. We charge a platform fee (typically 10-15%) 
              on completed trips. Payment methods include online payments (UPI, cards, net banking) and cash payments.
            </p>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">5.3 Cancellations</h3>
            <p>
              Cancellation policies vary by trip. Refunds for online payments are processed within 5-7 business days. 
              Cash payments are handled directly between rider and passenger.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. User Conduct</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Use the Platform for any illegal or unauthorized purpose</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Provide false or misleading information</li>
              <li>Interfere with or disrupt the Platform's operation</li>
              <li>Attempt to gain unauthorized access to the Platform</li>
              <li>Use automated systems to access the Platform</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
            <p>
              Violation of these rules may result in immediate account suspension or termination.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Limitation of Liability</h2>
            <p className="mb-4">
              SastaMastaRide acts as an intermediary platform connecting riders and passengers. We are not responsible for:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>The quality, safety, or legality of rides provided by riders</li>
              <li>The accuracy of information provided by users</li>
              <li>Any accidents, injuries, or damages during trips</li>
              <li>Disputes between riders and passengers</li>
              <li>Loss of property or personal belongings</li>
            </ul>
            <p>
              Users use the Platform at their own risk. Riders and passengers are responsible for their own safety 
              and insurance coverage.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Intellectual Property</h2>
            <p>
              All content on the Platform, including logos, text, graphics, and software, is the property of 
              SastaMastaRide or its licensors and is protected by copyright and trademark laws. You may not use, 
              reproduce, or distribute any content without our written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Termination</h2>
            <p className="mb-4">
              We reserve the right to suspend or terminate your account at any time, with or without cause or notice, 
              for violations of these Terms or for any other reason we deem necessary.
            </p>
            <p>
              You may terminate your account at any time by contacting our support team. Upon termination, your 
              right to use the Platform will immediately cease.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Dispute Resolution</h2>
            <p className="mb-4">
              Any disputes arising from or related to these Terms or the Platform shall be resolved through:
            </p>
            <ol className="list-decimal list-inside ml-4 space-y-1 mb-4">
              <li>Good faith negotiation between parties</li>
              <li>Mediation through our support team</li>
              <li>Arbitration in accordance with Indian Arbitration laws</li>
            </ol>
            <p>
              These Terms are governed by the laws of India, and any legal proceedings shall be subject to the 
              jurisdiction of courts in India.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Contact Information</h2>
            <p className="mb-4">
              For questions about these Terms, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Email:</strong> legal@sastamastaride.com</p>
              <p><strong>Address:</strong> SastaMastaRide, Managed by RKTECH Solutions, India</p>
              <p><strong>Phone:</strong> +91-1800-XXX-XXXX</p>
            </div>
          </section>

          <div className="bg-blue-50 p-6 rounded-lg mt-8">
            <p className="text-gray-700">
              By using SastaMastaRide, you acknowledge that you have read, understood, and agree to be bound by these 
              Terms of Service. If you do not agree to these Terms, please discontinue use of the Platform immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;

